import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useRouterStore } from 'apps/hooks';
import { useViewerWidth } from 'libs/rendering/hooks';
import RightClickMenu from 'components/RightClickMenu/RightClickMenu';
import { NavCube } from 'libs/rendering/gltf/components/navCube';
import { Legend } from 'libs/rendering/gltf/components/legend';

import {
  CadViewerStyled,
  PreviewCadViewerStyled,
} from 'libs/rendering/gltf/viewer.styled';
import { clientStore } from 'apps/client/app';
import { useCADManager } from 'hooks';

import { Canvas3DStyled, Separator } from './canvas3D.styled';
import { TwoSidedGltfDiff } from './TwoSidedGltfDiff';

const gestureType = {
  ZOOM: 'zoom',
  PAN: 'pan',
  UNKNOWN: 'unknown',
};

function dist(touchEv) {
  /* The distance between two simultaneous touches */
  return Math.hypot(
    touchEv[0].pageX - touchEv[1].pageX,
    touchEv[0].pageY - touchEv[1].pageY,
  );
}

function vectorAngle(ev1, ev2) {
  /* The direction of travel of a single touch between two events */
  const vectFingerOne = [
    ev2[0].pageX - ev1[0].pageX,
    ev2[0].pageY - ev1[0].pageY,
  ];
  const vectFingerTwo = [
    ev2[1].pageX - ev1[1].pageX,
    ev2[1].pageY - ev1[1].pageY,
  ];
  const magVectFOne = Math.sqrt(vectFingerOne[0] ** 2 + vectFingerOne[1] ** 2);
  const magVectFTwo = Math.sqrt(vectFingerTwo[0] ** 2 + vectFingerTwo[1] ** 2);
  const dotProduct =
    vectFingerOne[0] * vectFingerTwo[0] + vectFingerOne[1] * vectFingerTwo[1];
  if (magVectFOne === 0 || magVectFTwo === 0) {
    return null;
  }
  return Math.acos(dotProduct / (magVectFOne * magVectFTwo));
}

// Issues

// When removing a finger from Pan/Zoom should 1 finger touch to persist?
// This can be irritating with panning in particular

class ZoomState {
  gesturing = false;
  gesture = gestureType.UNKNOWN;
  prev = [];
  curr = [];

  GestureStart = (e, viewer) => {
    this.viewer = viewer;
    this.gesturing = true;
    this.rect = e.target.getBoundingClientRect();
    this.setTouch('prev', e);
    this.setTouch('curr', e);
  };

  setTouch(attr, e) {
    this[attr] = [
      {
        pageX: e.touches[0].pageX,
        pageY: e.touches[0].pageY,
      },
      {
        pageX: e.touches[1].pageX,
        pageY: e.touches[1].pageY,
      },
    ];
  }

  GestureMove = e => {
    switch (this.gesture) {
      case gestureType.ZOOM:
        this.prev = this.curr;
        this.setTouch('curr', e);
        this.Zoom();
        break;
      case gestureType.PAN:
        this.prev = this.curr;
        this.setTouch('curr', e);
        this.Pan();
        break;
      default:
        this.setTouch('curr', e);
        this.Classify();
    }
  };

  Classify = () => {
    /* Once there are two fingers down we should classify as soon as we have sufficient movement.
    If there is a sufficient move of both touchEv the same vector then classify as Pan

    Alternate More Efficient Implementation of Gesture Classification (though Vector Angle seems to work fine)


    const dPrev = Math.abs(this.prev[0].pageX - this.prev[1].pageX) + Math.abs(this.prev[0].pageY - this.prev[1].pageY); // distance btw fingers at start
    const dCurr =  Math.abs(this.curr[0].pageX - this.curr[1].pageX) + Math.abs(this.curr[0].pageY - this.curr[1].pageY); // distance btw fingers after move
    const zoomAmount = abs(dPrev - dCurr);
    if (zoomAmount > zoomThreshold) {
      this.Zoom()
    } else {
      const dragX =  Math.abs(this.curr[0].pageX - this.prev[0].pageX);
      const dragY = Math.abs(this.curr[0].pageY - this.prev[0].pageY);
      if abs(dragX) > DragThreshold) || abs(dragY) > DragThreshold {
        this.Pan()
      }
    }
    */

    const taxicabDist =
      Math.abs(this.curr[0].pageX - this.prev[0].pageX) +
      Math.abs(this.curr[0].pageY - this.prev[0].pageY) +
      Math.abs(this.curr[1].pageX - this.prev[1].pageX) +
      Math.abs(this.curr[1].pageY - this.prev[1].pageY);

    if (taxicabDist > 20) {
      const radians = vectorAngle(this.prev, this.curr);
      if (radians === null) {
        return;
      }
      if (radians > Math.PI / 3) {
        this.gesture = gestureType.ZOOM;
        this.Zoom();
      } else {
        this.gesture = gestureType.PAN;
        this.viewer.settings.isLeftClickOrbit = false;
        this.viewer.window.SetCursor(
          this.curr[0].pageX - this.rect.left,
          this.curr[0].pageY - this.rect.top,
        );
        this.viewer.window.MouseDown();
        this.Pan();
      }
    }
  };

  Zoom = () => {
    if (dist(this.prev) > dist(this.curr)) {
      this.viewer.window.MouseScroll(-1); // zoom out
    } else if (dist(this.prev) < dist(this.curr)) {
      this.viewer.window.MouseScroll(1); // zoom in
    }
  };

  Pan = () => {
    this.viewer.window.SetCursor(
      this.curr[0].pageX - this.rect.left,
      this.curr[0].pageY - this.rect.top,
    );
    this.viewer.window.MouseMove();
  };

  GestureEnd = () => {
    if (this.gesture === gestureType.PAN) {
      this.viewer.settings.isLeftClickOrbit = true;
      this.viewer.window.MouseUp();
    }
    this.gesturing = false;
    this.gesture = gestureType.UNKNOWN;
    this.prev = [];
    this.curr = [];
  };
}

// const legendSteps = 9;

const Canvas3D = observer(function _Canvas3D() {
  const { isTwoSided, viewerControl } = useCADManager();
  const [canvasWidth, rightOffset] = useViewerWidth();

  const routerStore = useRouterStore();
  const { route } = routerStore;
  const isViewerRoute = route.name === 'viewer';
  const [zoom] = useState(() => new ZoomState());

  const { viewer, file, isPreview, viewerVisible } = viewerControl;

  useEffect(() => {
    viewer?.updateCanvas();
  }, [canvasWidth, file, viewer]);

  // mouse events (disable device zoom)
  useEffect(() => {
    // This condition means we have a full screen CADRoom
    // better condition would be to have a dedicated viewerControl.isGlobalViewer
    if (!isPreview && viewerVisible) {
      /* Disable built in device zoom. Disable pull-down-to-refresh on android. */
      const viewport = document.querySelector('meta[name="viewport"]');
      viewport?.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
      );
      if (!isPreview) {
        document.documentElement.style.overflow = 'hidden';
        document.documentElement.style.overscrollBehavior = 'auto contain';
        document.body.style.touchAction = 'none';
      }
      return () => {
        // viewport = document.querySelector('meta[name="viewport"]');
        viewport?.setAttribute(
          'content',
          'width=device-width, initial-scale=1',
        );
        document.documentElement.style.overflow = 'auto';
        document.documentElement.style.overscrollBehavior = 'auto';
        document.body.style.touchAction = 'auto';
      };
    }
    return () => {};
  }, [viewerControl, isPreview, viewerVisible]);

  if (!viewer) return null;

  const {
    settings,
    selectionManager,
    session,
    module,
    window: viewerWindow,
  } = viewer;

  if (!settings || !selectionManager || !session || !module || !viewerWindow) {
    return null;
  }

  const selModeEnum = module.SelectionMode;

  const mousePointing =
    selectionManager.HighlightCount() > 0 || session.IsSomethingHover();

  /*
    event handling
  */
  function checkCanvasInput(e) {
    if (!viewer.isOk) return false;

    const isCanvas =
      e && e.target && e.target.id && e.target.id === viewerControl.canvasId;
    if (!isCanvas) {
      selectionManager.ClearHighlight();
      viewerWindow.CancelDrag();
    }
    return isCanvas;
  }

  function setCursorPosition(e) {
    const dpr = window.devicePixelRatio || 1.0;
    viewerWindow.SetCursor(
      e.nativeEvent.offsetX * dpr,
      e.nativeEvent.offsetY * dpr,
    );
  }

  function setTouchPosition(e, callback) {
    const touch = e.touches[0];
    if (!checkCanvasInput(e) || !touch) return;

    const rect = e.target.getBoundingClientRect();
    viewerWindow.SetCursor(touch.pageX - rect.left, touch.pageY - rect.top);
    callback();
  }

  function touchStart(e) {
    if (!viewerControl.cadReady) return false;
    if (e.touches.length === 1) {
      setTouchPosition(e, viewerWindow.MouseDown);
    } else if (e.touches.length === 2) {
      zoom.GestureStart(e, viewer);
    }
    return false;
  }

  function touchMove(e) {
    if (!viewerControl.cadReady) return false;
    if (zoom.gesturing) {
      zoom.GestureMove(e);
    } else {
      setTouchPosition(e, viewerWindow.MouseMove);
    }
    return false;
  }

  function touchEnd(e) {
    if (!viewerControl.cadReady) return false;
    if (zoom.gesturing) {
      zoom.GestureEnd(e);
    }
    setTouchPosition(e, viewerWindow.MouseUp);
    if (e.touches.length === 1) {
      // allow the user to remove a finger from a pinch and switch to dragging and dropping
      setTouchPosition(e, viewerWindow.MouseDown);
    }
    return false;
  }

  function touchCancel(e) {
    /* OS level event e.g. orientation change - untested */
    if (!viewerControl.cadReady) return;
    if (viewer.isOk) {
      selectionManager.ClearHighlight();
      viewerWindow.CancelDrag();
    }
    zoom.GestureEnd(e);
  }

  function onDoubleClick(e) {
    if (isPreview) return;
    const selMode = selectionManager.GetSelectionMode();
    if (selMode !== selModeEnum.SelectionModeLink) {
      const cadItem = viewerControl.hoveredCadItem;
      viewerControl.InspectCadItem(cadItem);
    }
  }

  function mouseDown(e) {
    if (!viewerControl.cadReady) return;
    if (!checkCanvasInput(e)) return;

    if (e.buttons === 1) {
      // left click (camera orbit)
      setCursorPosition(e);
      viewerWindow.MouseDown();
    } else if (e.button === 2) {
      // right click...
      if (!viewerControl.contextMenuOpened) {
        // activate pan
        setCursorPosition(e);
        viewerWindow.MouseRightDown();

        // - viewerControl.contextMenuOpened needs to either false or true in mouseUp
        // - when we're setting it here then we rely on a side effect in contextMenuOptions
        // which runs on canvas render, which sets viewerControl.contextMenuOpened = false
        // when there are no contextMenuOptions, so when we clicked on empty space and we
        // are not hovering any items
        // - viewerControl.contextMenuOpened itself is actually never used anywhere to check
        // if we should render RightClickMenu, only the result of contextMenuOptions, the
        // cadOptions, is used to determine if should render, if the cadOptions are empty we
        // won't render the RightClickMenu
        // - so the logic is:
        // 1. set contextMenuOpened here anticipating that we might open it
        // 2. have the canvas re-render and check if we're hovering something
        //   2.1 if we're hovering something then cadOptions.length > 0 and we render
        //   the context menu
        //   2.2 the context menu appears under the mouse pointer and we're preventing this.mouseUp
        //   from running because the context menu handles it
        //   2.3 the context menu is open, the mouseUp happened without closing it, it handles events
        //   until we leave it and it is closed on mouseLeave, or alternatively a canvas render happens
        // 3. if we don't hover anything then mouseUp runs and sets
        // viewerControl.contextMenuOpened = false
        // TODO:
        // 1. rework all of this so that contextMenuOptions is a useEffect that runs on
        // changes to hoveredCadItem
        // 2. make mouseUp trigger contextMenuOpened by checking if we're hovering something
        // 3. I would do it right now, but I can't because I can't observe changes to hoveredCadItem
        // and need to refactor the code to make that work
        viewerControl.contextMenuOpened = true;
      }
    }
    e.stopPropagation();
  }

  function mouseUp(e) {
    if (!viewerControl.cadReady) return;
    if (!checkCanvasInput(e)) return;

    if (e.button === 0) {
      // left click (camera orbit)
      setCursorPosition(e);
      viewerWindow.MouseUp();
    } else if (e.button === 2) {
      // right click
      if (!viewerControl.contextMenuOpened) {
        setCursorPosition(e);
        viewerWindow.MouseRightUp();
      }
    }
    e.stopPropagation();
  }

  function mouseMove(e) {
    if (!viewerControl.cadReady) return;
    if (!checkCanvasInput(e)) return;

    setCursorPosition(e);
    viewerWindow.MouseMove();
    e.stopPropagation();
  }

  function mouseLeave(e) {
    if (!viewerControl.cadReady) return;
    if (viewer.isOk) {
      // check if still inside canvas
      //  (some children e.g. pin note has been entered...)
      const canvas = e.currentTarget;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect) return;

      selectionManager.ClearHighlight();
      viewerWindow.CancelDrag();
    }
  }

  function mouseWheel(e) {
    if (!viewerControl.cadReady) return;
    if (!checkCanvasInput(e)) return;

    setCursorPosition(e);

    // (camera zoom)
    let delta = e.deltaY;
    if (e.ctrlKey) {
      // zoom gesture on a trackpad
      delta = -delta;
    }
    if (delta > 0) {
      viewerWindow.MouseScroll(1); // zoom in
    } else if (delta < 0) {
      viewerWindow.MouseScroll(-1); // zoom out
    }

    // e.preventDefault(); // don't scroll the page
    // Above line is commented because it throws error
    // "Unable to preventDefault inside passive event listener due to target being treated as passive.""
    e.stopPropagation();
  }

  function keyPress(e) {
    e.preventDefault(); // don't scroll the page if arrow keys
  }

  function onContextMenu(e) {
    // don't show context menu on right click
    e.preventDefault();
  }

  function hoverOnTop(item) {
    selectionManager?.ClearHighlight();

    if (!item) {
      viewerControl?.SetHighlightBehaviour(null, null);
      if (viewerControl.contextMenuItem) {
        selectionManager?.AddHighlightItem(viewerControl.contextMenuItem._wasm);
      }
      selectionManager.reportWasmChange();
      return;
    }

    selectionManager?.AddHighlightItem(item._wasm);
    item.highlighted = false; // do not affect tree view...
    viewerControl.SetHighlightBehaviour('on-front', item);
    selectionManager.reportWasmChange();
  }

  const contextMenuOptions = () => {
    if (!viewerControl.contextMenuOpened) return [];
    if (isPreview) return [];
    if (!viewerControl.cadReady) return [];

    const {
      cadFile,
      hoveredTarget: hoverItem,
      hoveredCadItem: cadItem,
    } = viewerControl;

    const cadOptions = [];
    if (cadItem && hoverItem && !cadItem.IsBrepItem()) {
      const body = cadItem.GetBody();
      const mesh = body?.GetBodyMesh();
      if (mesh != null) {
        if (viewer.isDiffing) {
          // load brep data to get scale...
          viewerControl.ParseInstancePmi(cadItem);
          const unitScale = body.GetScale();

          const t = viewer.diffTool.GetTransformation(
            cadItem.selfIx,
            hoverItem.cadFileId,
          );
          if (!t.isNull) {
            // diff transformation info...
            if (t.hasRotation) {
              const axisAng = t.GetAxisAngle();
              cadOptions.push({
                label: 'rotation',
                transparent: true,
                extraLabel: `${parseFloat(axisAng.w.toFixed(2))}°`,
              });
            }
            if (t.hasTranslation) {
              const vec = t.GetTranslation();
              const options = [
                {
                  label: 'Δx',
                  extraLabel: `${parseFloat(
                    (vec.x * unitScale).toFixed(2),
                  )} mm`,
                  autoWidth: true,
                  transparent: true,
                },
                {
                  label: 'Δy',
                  extraLabel: `${parseFloat(
                    (vec.y * unitScale).toFixed(2),
                  )} mm`,
                  autoWidth: true,
                  transparent: true,
                },
                {
                  label: 'Δz',
                  extraLabel: `${parseFloat(
                    (vec.z * unitScale).toFixed(2),
                  )} mm`,
                  autoWidth: true,
                  transparent: true,
                },
              ];
              cadOptions.push({
                label: 'translation',
                extraLabel: `${parseFloat(
                  (t.GetTranslationAmmount() * unitScale).toFixed(2),
                )} mm`,
                transparent: true,
                options,
              });
            }
            if (t.hasScale) {
              const vec = t.GetScale();
              const options = [
                {
                  label: 'x',
                  extraLabel: `${parseFloat(vec.x.toFixed(3))}`,
                  autoWidth: true,
                  transparent: true,
                },
                {
                  label: 'y',
                  extraLabel: `${parseFloat(vec.y.toFixed(3))}`,
                  autoWidth: true,
                  transparent: true,
                },
                {
                  label: 'z',
                  extraLabel: `${parseFloat(vec.z.toFixed(3))}`,
                  autoWidth: true,
                  transparent: true,
                },
              ];
              cadOptions.push({
                label: 'scale',
                options,
                transparent: true,
              });
            }
          }
        } else {
          const instanceCount = cadFile.assembly?.InstanceCount();
          if (instanceCount > 1) {
            cadOptions.push({
              icon: cadItem.visible ? 'view_off' : 'view', // visibility
              onClick: () => {
                cadItem.visible = !cadItem.visible;
                viewerControl.ForceRender();
              },
              label: cadItem.visible ? 'Hide' : 'Show',
            });
            cadOptions.push({
              icon: 'expand', // inspect, new tab
              onClick: () => {
                viewerControl.InspectCadItem(cadItem);
              },
              label: 'New Tab',
              closeAfter: true,
            });
          }

          cadOptions.push({
            icon: 'cube', // Extract part and create rfq
            onClick: () => {
              const newpath = routerStore.route.name;
              routerStore.navigate(newpath, {
                ...routerStore.route.params,
                panel: 'rfq',
              });
              if (instanceCount > 1) {
                viewerControl.InspectCadItem(cadItem);
              }
            },
            label: 'Make Part',
            closeAfter: true,
          });

          const subAssemblyOptions = [];
          const currCamera = settings.CurrentCamera();
          const currentSubAssembly = currCamera.GetSubAssembly();

          let parent = cadItem.GetProduct();
          while (parent != null && !parent.IsRoot()) {
            if (parent.IsSubassembly() && parent.ChildCount() > 1) {
              const subAssembly = parent;
              if (
                currentSubAssembly == null ||
                currentSubAssembly.uuid !== subAssembly.uuid
              ) {
                subAssemblyOptions.push({
                  icon: 'expand', // inspect, new sub-assembly tab
                  onClick: () => viewerControl.InspectCadItem(subAssembly),
                  label: subAssembly.name,
                  closeAfter: true,
                  onMouseEnter: () => hoverOnTop(subAssembly),
                  onMouseLeave: () => hoverOnTop(null),
                });
              }
            }
            parent = parent.GetParent();
          }
          if (subAssemblyOptions.length > 0) {
            cadOptions.push({
              icon: 'folder',
              label: 'sub-assemblies',
              options: subAssemblyOptions,
              onMouseEnter: () => hoverOnTop(null),
              onMouseLeave: () => hoverOnTop(null),
            });
          }
        }
      }
    }

    if (cadOptions.length === 0) {
      viewerControl.contextMenuOpened = false;
    }

    // - we render the context menu with dark background, but when we're diffing
    // we're rendering it with white background, that seems like we should just decide
    // on a background color and stick with it
    const isDark = !viewer.isDiffing;
    return { cadOptions, isDark };
  };

  if (isPreview) {
    // - we come here either when a preview is embedded into the site somewhere, or
    // when we generate previews using the viewer/ endpoint
    // - in the viewer/ endpoint the canvas sits inside of something that has no
    // height and width(?), so using 100% for height and width (the default for
    // PreviewCadViewerStyled) does not work, in fact it crashes the viewer
    // - so I manually pass in the window.innerWidth/Height when we're in the viewer/ route
    // so that the PreviewCadViewerStyled can set a fixed size for the canvas in that case
    // - in all other cases we're embedded and it should just use 100%, so I pass null
    const previewWidth = isViewerRoute ? window.innerWidth : null;
    const previewHeight = isViewerRoute ? window.innerHeight : null;
    return (
      <>
        <PreviewCadViewerStyled
          id={viewerControl.wrapperId}
          width={previewWidth}
          height={previewHeight}
          tabIndex='0'
          itemScope
          itemType='http://schema.org/CreativeWork'
        >
          <>
            <Canvas3DStyled
              tabIndex='0'
              id={viewerControl.canvasId}
              width='100%'
              onMouseUp={mouseUp}
              onMouseDown={mouseDown}
              onMouseMove={mouseMove}
              onMouseLeave={mouseLeave}
              onTouchStart={touchStart}
              onTouchEnd={touchEnd}
              onTouchMove={touchMove}
              onTouchCancel={touchCancel}
              onWheel={mouseWheel}
              onKeyDown={keyPress}
              onContextMenu={onContextMenu}
              mousePointing={mousePointing}
              isInAnnotationMode={false}
              hidden={!viewerVisible}
            />
            {isTwoSided && <Separator />}
          </>
        </PreviewCadViewerStyled>
      </>
    );
  }

  if (viewerControl.viewerFailed || viewerControl.conversionProblem) {
    return null;
  }

  const { cadOptions, isDark } = contextMenuOptions();

  return (
    <>
      <CadViewerStyled
        className='Canvas3DWrapper'
        id={viewerControl.wrapperId}
        tabIndex='0'
        itemScope
        top={clientStore.PageHeaderHeight}
        itemType='http://schema.org/CreativeWork'
      >
        <Canvas3DStyled
          data-testid='Canvas3DStyled'
          tabIndex='0'
          id={viewerControl.canvasId}
          width={canvasWidth}
          onDoubleClick={onDoubleClick}
          onMouseUp={mouseUp}
          onMouseDown={mouseDown}
          onMouseMove={mouseMove}
          onMouseLeave={mouseLeave}
          onTouchStart={touchStart}
          onTouchEnd={touchEnd}
          onTouchMove={touchMove}
          onTouchCancel={touchCancel}
          onWheel={mouseWheel}
          onKeyDown={keyPress}
          onContextMenu={onContextMenu}
          mousePointing={mousePointing}
          isInAnnotationMode={selectionManager.IsSelectionModeLink() || false}
          hidden={!viewerVisible}
        />
        {viewerVisible && (
          <>
            <NavCube right={rightOffset} />

            <Legend />

            {isTwoSided && <TwoSidedGltfDiff />}
          </>
        )}
      </CadViewerStyled>
      {viewerVisible && <RightClickMenu isDark={isDark} options={cadOptions} />}
    </>
  );
});

export default Canvas3D;
